import React, { type FC } from 'react';
import { makeCopyShortcut } from 'utils/keyboardShortcuts';
import { makePath } from 'utils/routes';

import { CurrencyDisplay } from '@fragment/ui/src/components/Currency';
import { Icon } from '@fragment/ui/src/components/Icon';
import { IdCopyButton } from '@fragment/ui/src/components/IdCopyButton';
import type { ColumnDefinition } from '@fragment/ui/src/components/Table';
import { NavTable } from '@fragment/ui/src/components/Table';
import { TableHoverDetailRow } from '@fragment/ui/src/components/TableHoverDetailRow/TableHoverDetailRow';

import type { ExternalAccountNode } from './types';

const emptyValue = {
  'External Account': 'No External Accounts',
  Currency: 'N/A',
};

type ExternalAccountTableProps = {
  linkId: string;
  data: ExternalAccountNode[];
};

const schema: ColumnDefinition<ExternalAccountNode>[] = [
  {
    title: 'External Account',
    format: (row) => (
      <TableHoverDetailRow
        detail={<IdCopyButton type="externalId" identifier={row.externalId} />}
      >
        <div className="flex flex-row items-center truncate">
          <div className="flex-shrink-0">
            <Icon type="externalAccount" size="sm" />
          </div>
          <div className="truncate">{row.name}</div>
        </div>
      </TableHoverDetailRow>
    ),
    colspan: 'col-span-3',
  },
  {
    title: 'Currency',
    justify: 'right',
    format: (value) => (
      <CurrencyDisplay
        currencyMode={value.currencyMode}
        currency={value.currency ?? undefined}
      />
    ),
    colspan: 'col-span-1',
  },
];

export const ExternalAccountTable: FC<ExternalAccountTableProps> = ({
  linkId,
  data,
}) => {
  const getPath = (row: ExternalAccountNode) =>
    makePath(['links', 'id', linkId, 'accounts', row.id]);
  return (
    <NavTable
      borderedRows
      dottedHeader={false}
      keyboardShortcuts={makeCopyShortcut('externalId')}
      textSize="md"
      {...{ data, schema, getPath, emptyValue }}
      stickyHeader={false}
    />
  );
};
